import { computed, ref, unref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { useShipmentFormFlowBase } from '@/composables/useShipmentFormFlowBase';
import { useShipmentFormCommit } from '@/composables/useShipmentFormCommit';
import { useShipmentFormNameAutofill } from '@/composables/useShipmentFormNameAutofill';
import type { ShipmentFormGeneralErrors } from '@/composables/useShipmentFormValidation';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import { CREATE_SAVE_POLICY } from '@/composables/shipment-form-save-policy';
import {
  buildShipmentSavePlanFromChangeContext,
  changeSetFromSavePlan,
} from '@/composables/shipment-form-change-set';
import {
  assertRoutePlanNotStale,
  assertRoutingNotBusy,
  changeSetNeedsStopsValidation,
} from '@/composables/shipment-form-save-guards';
import { captureRoutePersistIntent } from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';
import { executeShipmentFormSave } from '@/composables/shipment-form-save-orchestrator';
import * as events from '@/events';

export function useShipmentCreateFlow(options: {
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  generalErrors: Ref<ShipmentFormGeneralErrors>;
}) {
  const {
    vuexStore,
    accountId,
    formStore,
    shipmentId,
    validation,
    formSave,
    emptyGeneralErrors,
  } = useShipmentFormFlowBase({
    stopsPort: options.stopsPort,
    generalErrors: options.generalErrors,
  });

  const {
    generalName,
    generalCarrier,
    generalNotes,
    generalAssetValueCents,
    selectedDeviceIds,
    selectedDevices,
    stops,
    persistedStopsPayloadSnapshot,
    routePlan,
    corridorBufferM,
    geometryQualityPercent,
    serverHydratedRouteFingerprint,
  } = storeToRefs(formStore);

  const router = useRouter();
  const toast = useToast();
  const wizardStep = ref(0);
  const stepBusy = ref(false);

  const commit = useShipmentFormCommit({
    accountId,
    formSave,
    validation,
  });
  const nameAutofill = useShipmentFormNameAutofill();

  const canContinueStepTracker = computed(
    () => selectedDeviceIds.value.length > 0,
  );

  const canContinueStepGeneral = computed(
    () =>
      Boolean(generalName.value?.trim()) && validation.isPlannedRangeComplete(),
  );

  function resetWizardStep() {
    wizardStep.value = 0;
  }

  function goBack() {
    if (wizardStep.value > 0) {
      wizardStep.value -= 1;
    }
  }

  function onCancel() {
    router.push({
      name: 'ShipmentListView',
      query: { search: vuexStore.getters['shipment/search'] },
    });
  }

  async function onContinueStepTracker() {
    if (
      !validation.validateDeviceSelection(
        selectedDevices.value.map((device) => ({
          value: device.id,
          label: device.label,
        })),
      )
    ) {
      toast.error('Please select a tracker before continuing.');
      return;
    }

    stepBusy.value = true;
    formStore.clearServerFieldErrors();
    try {
      nameAutofill.applyFromPrimaryDevice();

      if (
        shipmentId.value != null &&
        formStore.haveDeviceIdsChangedSincePersisted(selectedDeviceIds.value)
      ) {
        await commit.commitDevicesIfDirty();
      }

      wizardStep.value = 1;
    } catch (err) {
      validation.handleFormApiError(err, 'Could not save tracker.');
    } finally {
      stepBusy.value = false;
    }
  }

  async function onContinueStepGeneral() {
    if (
      !validation.validateGeneralFields({
        requirePlannedRange: true,
      })
    ) {
      toast.error('Please fix the General section before continuing.');
      return;
    }

    stepBusy.value = true;
    formStore.clearServerFieldErrors();
    try {
      if (shipmentId.value == null) {
        await commit.ensureDraftWithDevices();
      } else {
        await commit.commitMetadataIfDirty();
      }
      wizardStep.value = 2;
    } catch (err) {
      validation.handleFormApiError(err, 'Could not save draft.');
    } finally {
      stepBusy.value = false;
    }
  }

  async function onContinue() {
    formStore.clearServerFieldErrors();
    if (wizardStep.value === 0) {
      await onContinueStepTracker();
      return;
    }
    if (wizardStep.value === 1) {
      await onContinueStepGeneral();
    }
  }

  async function onPublish() {
    const sid = shipmentId.value;
    if (sid == null) {
      toast.error('Missing shipment.');
      return;
    }

    if (
      !assertRoutingNotBusy(
        validation,
        () => formSave.isRoutingBusy(),
        'Wait for the route to finish calculating before publishing.',
      )
    ) {
      return;
    }

    const validate = options.stopsPort.value?.validateStops?.();
    if (validate) {
      const stopsOk = await validate;
      if (stopsOk === false) {
        toast.error('Please fix the Stops section before publishing.');
        return;
      }
    }

    const selectedByPathId =
      unref(options.stopsPort.value?.selectedByPathId) ?? {};

    const plan = buildShipmentSavePlanFromChangeContext(
      {
        metadataChanged: formStore.haveMetadataChangedSincePersisted(
          generalName.value,
          generalCarrier.value,
          generalNotes.value,
          generalAssetValueCents.value,
        ),
        stops: stops.value,
        stopsChangedSincePersisted: formStore.haveStopsChangedSincePersisted(
          stops.value,
        ),
        persistedStopsPayloadSnapshot: persistedStopsPayloadSnapshot.value,
        deviceIdsChanged: false,
        selectedByPathId,
        routePlan: routePlan.value,
      },
      CREATE_SAVE_POLICY,
      { includePublish: true, createPublishRouteUpsert: true },
    );
    const changeSet = changeSetFromSavePlan(plan);

    if (changeSetNeedsStopsValidation(changeSet)) {
      if (
        !assertRoutePlanNotStale(
          changeSet,
          stops.value,
          serverHydratedRouteFingerprint.value,
        )
      ) {
        toast.error(
          'Wait for the route to finish calculating before publishing.',
        );
        return;
      }
    }

    stepBusy.value = true;
    formStore.clearServerFieldErrors();
    try {
      const routeIntent = captureRoutePersistIntent({
        mode: plan.routes.mode,
        routePlan: routePlan.value,
        selectedByPathId,
        corridorBufferM: corridorBufferM.value,
        geometryQualityPercent: geometryQualityPercent.value,
        stops: stops.value,
      });

      await executeShipmentFormSave(
        formSave.getPersistDeps(),
        {
          accountId,
          shipmentId,
          stopsPort: options.stopsPort,
          deviceIds: selectedDeviceIds,
          canEditMetadata: true,
          canEditRoute: true,
          canEditTracker: false,
          metadataPayload: validation.normalizeMetadataPayload(),
          deviceIdsToAssign: selectedDeviceIds.value,
        },
        {
          mode: 'create-publish',
          plan,
          changeSet,
          routeIntent,
          deviceIdsOverride: selectedDeviceIds.value,
          suspendHydration: false,
          deferInvalidation: true,
          includePublish: true,
        },
      );

      events.trigger(events.names.SHIPMENT_CREATED, {
        id: sid,
        name: generalName.value,
      });
      router.replace({
        name: 'ShipmentDetailsView',
        params: { id: sid },
      });
    } catch (err) {
      validation.handleFormApiError(err, 'Could not publish shipment.');
    } finally {
      stepBusy.value = false;
    }
  }

  function resetForm() {
    formStore.resetForm();
    resetWizardStep();
    options.generalErrors.value = emptyGeneralErrors();
    vuexStore.dispatch('geofence/fetch');
  }

  return {
    wizardStep,
    stepBusy,
    canContinueStepTracker,
    canContinueStepGeneral,
    onContinue,
    onPublish,
    onCancel,
    goBack,
    resetForm,
  };
}
